import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, DateTimeInput, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

export default function OverrideDatesModal({ subscriptionLink, organizationId, onClose, onSuccess }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const locale = i18n.language;

    const [startDate, setStartDate] = React.useState(subscriptionLink?.start_date_override || null);
    const [endDate, setEndDate] = React.useState(subscriptionLink?.end_date_override || null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [dateError, setDateError] = React.useState(null);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        subscriptionLink
            .override_dates({
                organizationId,
                start_date_override: startDate,
                end_date_override: endDate
            })
            .then(() => {
                toast.success(t('ticket.webservice.subscriptionLink.overrideDatesSuccess'));
                onSuccess();
            })
            .catch(() => {
                toast.error(t('ticket.webservice.subscriptionLink.overrideDatesFailure'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [subscriptionLink, organizationId, startDate, endDate, onSuccess]);

    const handleReset = React.useCallback(() => {
        setStartDate(null);
        setEndDate(null);
        setDateError(null);
    }, []);

    const handleStartDateChange = React.useCallback(
        date => {
            setStartDate(date);
            setDateError(null);
            // Clear error if dates are now valid
            if (endDate && date && endDate <= date) {
                setDateError(t('ticket.webservice.subscriptionLink.beforeStartDateError'));
            }
        },
        [endDate]
    );

    const handleEndDateChange = React.useCallback(
        date => {
            setEndDate(date);
            // Validate and set error if end date is before start date
            if (startDate && date && date <= startDate) {
                return setDateError(t('ticket.webservice.subscriptionLink.beforeStartDateError'));
            }
            return setDateError(null);
        },
        [startDate]
    );

    return (
        <Modal
            size="medium"
            open={true}
            onClose={onClose}
            title={t('ticket.webservice.subscriptionLink.overrideDatesTitle')}
            submitButton={<Button primary onClick={handleSubmit} label={t('ticket.common.action.save')} loading={isSubmitting} disabled={isSubmitting || dateError} />}
            cancelButton={<Button onClick={onClose} label={t('ticket.common.action.cancel')} disabled={isSubmitting} />}
        >
            <Form>
                <Form.Field>
                    <DateTimeInput
                        timeLabel={t('ticket.common.time.start')}
                        dateLabel={t('ticket.webservice.subscriptionLink.startDateOverride')}
                        onChange={handleStartDateChange}
                        value={startDate}
                        locale={locale}
                        placeholder={subscriptionLink?.start_date || t('ticket.webservice.subscriptionLink.defaultStartDate')}
                    />
                </Form.Field>

                <Form.Field error={dateError ? true : false}>
                    <DateTimeInput
                        timeLabel={t('ticket.common.time.end')}
                        dateLabel={t('ticket.webservice.subscriptionLink.endDateOverride')}
                        onChange={handleEndDateChange}
                        value={endDate}
                        locale={locale}
                        placeholder={subscriptionLink?.end_date || t('ticket.webservice.subscriptionLink.defaultEndDate')}
                        dateProps={{ minDate: startDate }}
                    />
                    {dateError && <Form.FieldErrors errors={[dateError]} />}
                </Form.Field>

                <Form.Field>
                    <Button inverted onClick={handleReset} label={t('ticket.webservice.subscriptionLink.resetToDefault')} disabled={isSubmitting} />
                </Form.Field>
            </Form>
        </Modal>
    );
}

OverrideDatesModal.propTypes = {
    subscriptionLink: PropTypes.object.isRequired,
    organizationId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
};
